import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Toolbar,
  ToolbarGroup,
  Title3,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  Hamburger,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import TopBarContext from '../contexts/TopBarContext';
import { splitTopBarActions } from './topBarActions';
import type { SidebarMode } from './Sidebar';
import './TopBar.css';

interface TopBarProps {
  onSidebarToggle: () => void;
  sidebarMode: SidebarMode;
}

const TopBar: React.FC<TopBarProps> = ({ onSidebarToggle, sidebarMode }) => {
  const { title, titleNode, actions, actionItems, breadcrumbs } = useContext(TopBarContext);
  const navigate = useNavigate();
  const splitActions = actionItems ? splitTopBarActions(actionItems) : null;
  const overflowTriggerRef = splitActions?.overflow.find(action => action.triggerRef)?.triggerRef;

  const hasBreadcrumbs = breadcrumbs.length > 0;

  return (
    <div className="topbar-container">
      <Toolbar
        aria-label="Application toolbar"
        className="topbar-toolbar"
        style={{ alignItems: 'center' }}
      >
        <ToolbarGroup style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0 }}>
          {sidebarMode === 'hidden' && (
            <Hamburger onClick={onSidebarToggle} style={{ marginRight: 8, paddingLeft: 9}} />
          )}
          <div className="topbar-heading-row">
            {hasBreadcrumbs && (
              <>
                <Breadcrumb
                  aria-label="Breadcrumb navigation"
                  size="small"
                  className="topbar-breadcrumb"
                >
                  {breadcrumbs.map((item, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    // Skip last breadcrumb if we have a title to show instead
                    if (isLast && (titleNode || title)) return null;
                    return (
                      <React.Fragment key={index}>
                        <BreadcrumbItem>
                          {item.to ? (
                            <BreadcrumbButton
                              onClick={() => navigate(item.to!)}
                              current={false}
                            >
                              {item.text}
                            </BreadcrumbButton>
                          ) : (
                            <BreadcrumbButton current>
                              {item.text}
                            </BreadcrumbButton>
                          )}
                        </BreadcrumbItem>
                        <BreadcrumbDivider />
                      </React.Fragment>
                    );
                  })}
                </Breadcrumb>
                {(titleNode || title) && (
                  <>
                    <span className="topbar-breadcrumb-title">
                      {titleNode || title}
                    </span>
                  </>
                )}
              </>
            )}

            {!hasBreadcrumbs && (titleNode ? (
              <div className="topbar-title">{titleNode}</div>
            ) : title ? (
              <Title3 className="topbar-title">
                {title}
              </Title3>
            ) : null)}
          </div>
        </ToolbarGroup>

        <div style={{ flex: 1 }} />
        {(actionItems || actions) && (
          <ToolbarGroup className="topbar-actions">
            {splitActions ? <>
              {splitActions.inline.map(action => (
                <Button key={action.id} ref={action.triggerRef} appearance={action.appearance ?? 'outline'}
                  icon={action.icon} disabled={action.disabled} onClick={() => void action.onClick()}>
                  {action.label}
                </Button>
              ))}
              {splitActions.overflow.length > 0 && (
                <Menu positioning="below-end">
                  <MenuTrigger disableButtonEnhancement>
                    <Button ref={overflowTriggerRef} appearance="subtle" icon={<MoreHorizontalRegular />} aria-label="More actions" />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      {splitActions.overflow.map(action => (
                        <MenuItem key={action.id} icon={action.icon} disabled={action.disabled}
                          onClick={() => void action.onClick()}>{action.label}</MenuItem>
                      ))}
                    </MenuList>
                  </MenuPopover>
                </Menu>
              )}
            </> : actions}
          </ToolbarGroup>
        )}
      </Toolbar>
    </div>
  );
};

export default TopBar;